<!-- RevenueHistoryModal.vue -->
<template>
  <div class="modal fade" id="revenueHistoryModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-chart-line me-2"></i>
            Revenue History - {{ taxpayer.name }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Summary Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card border">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1 overflow-hidden">
                      <p class="text-truncate font-size-14 mb-2">Total Revenue (12 months)</p>
                      <h4 class="mb-0">{{ formatPrice(revenueStats.totalRevenue) }}</h4>
                    </div>
                    <div class="text-primary ms-auto">
                      <i class="ri-money-dollar-circle-line font-size-24"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card border">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1 overflow-hidden">
                      <p class="text-truncate font-size-14 mb-2">Average Monthly</p>
                      <h4 class="mb-0">{{ formatPrice(revenueStats.averageMonthly) }}</h4>
                    </div>
                    <div class="text-success ms-auto">
                      <i class="ri-line-chart-line font-size-24"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card border">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1 overflow-hidden">
                      <p class="text-truncate font-size-14 mb-2">Growth Rate</p>
                      <h4 class="mb-0 text-success">+{{ revenueStats.growthRate }}%</h4>
                    </div>
                    <div class="text-success ms-auto">
                      <i class="ri-arrow-up-line font-size-24"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card border">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1 overflow-hidden">
                      <p class="text-truncate font-size-14 mb-2">Payment Rate</p>
                      <h4 class="mb-0">{{ revenueStats.paymentRate }}%</h4>
                    </div>
                    <div class="text-info ms-auto">
                      <i class="ri-percent-line font-size-24"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="card border mb-4">
            <div class="card-header">
              <h6 class="mb-0">Filter Options</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Period:</label>
                  <select v-model="filters.period" @change="filterData" class="form-select">
                    <option value="12">Last 12 Months</option>
                    <option value="6">Last 6 Months</option>
                    <option value="3">Last 3 Months</option>
                    <option value="24">Last 24 Months</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">Revenue Stream:</label>
                  <select v-model="filters.revenueStream" @change="filterData" class="form-select">
                    <option value="">All Streams</option>
                    <option value="SBP">Single Business Permit</option>
                    <option value="Liquor License">Liquor License</option>
                    <option value="Land Rates">Land Rates</option>
                    <option value="Billboard">Billboard Advertising</option>
                    <option value="Market Fees">Market Fees</option>
                    <option value="Parking Fees">Parking Fees</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">Location:</label>
                  <select v-model="filters.location" @change="filterData" class="form-select">
                    <option value="">All Locations</option>
                    <option v-for="location in taxpayer.locations" :key="location" :value="location">
                      {{ location }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">Status:</label>
                  <select v-model="filters.status" @change="filterData" class="form-select">
                    <option value="">All Status</option>
                    <option value="PAID">Paid</option>
                    <option value="UNPAID">Unpaid</option>
                    <option value="OVERDUE">Overdue</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Revenue Chart -->
          <div class="card border mb-4">
            <div class="card-header">
              <h6 class="mb-0">Revenue Trend</h6>
            </div>
            <div class="card-body">
              <div class="chart-container" style="height: 300px;">
                <canvas id="revenueChart"></canvas>
              </div>
            </div>
          </div>

          <!-- Monthly Breakdown -->
          <div class="card border">
            <div class="card-header">
              <h6 class="mb-0">Monthly Revenue Breakdown</h6>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-dark">
                  <tr>
                    <th>Month</th>
                    <th>Revenue Stream</th>
                    <th>Location</th>
                    <th class="text-end">Billed Amount</th>
                    <th class="text-end">Paid Amount</th>
                    <th class="text-end">Outstanding</th>
                    <th>Status</th>
                    <th>Payment Date</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in filteredRevenueHistory" :key="record.id">
                    <td>{{ record.month }}</td>
                    <td>
                      <span class="badge bg-primary">{{ record.revenueStream }}</span>
                    </td>
                    <td>{{ record.location }}</td>
                    <td class="text-end">{{ formatPrice(record.billedAmount) }}</td>
                    <td class="text-end">{{ formatPrice(record.paidAmount) }}</td>
                    <td class="text-end">
                      <span :class="record.outstanding > 0 ? 'text-danger fw-bold' : 'text-success'">
                        {{ formatPrice(record.outstanding) }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusClass(record.status)">
                        {{ record.status }}
                      </span>
                    </td>
                    <td>{{ record.paymentDate || '-' }}</td>
                  </tr>
                  </tbody>
                  <tfoot class="table-dark">
                  <tr>
                    <th colspan="3">Total ({{ filteredRevenueHistory.length }} records)</th>
                    <th class="text-end">{{ formatPrice(totals.billed) }}</th>
                    <th class="text-end">{{ formatPrice(totals.paid) }}</th>
                    <th class="text-end">{{ formatPrice(totals.outstanding) }}</th>
                    <th colspan="2"></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="downloadReport" class="btn btn-success">
            <i class="mdi mdi-download me-1"></i>
            Download Report
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "RevenueHistoryModal",
  props: {
    taxpayer: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      filters: {
        period: "12",
        revenueStream: "",
        location: "",
        status: ""
      },

      // Mock revenue history data
      revenueHistory: [
        {
          id: 1,
          month: "2025-01",
          revenueStream: "SBP",
          location: "Nairobi - CBD",
          billedAmount: 150000,
          paidAmount: 150000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2025-01-15"
        },
        {
          id: 2,
          month: "2025-01",
          revenueStream: "Billboard",
          location: "Nairobi - CBD",
          billedAmount: 80000,
          paidAmount: 80000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2025-01-10"
        },
        {
          id: 3,
          month: "2024-12",
          revenueStream: "SBP",
          location: "Nairobi - CBD",
          billedAmount: 150000,
          paidAmount: 150000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-12-20"
        },
        {
          id: 4,
          month: "2024-12",
          revenueStream: "Parking Fees",
          location: "Nairobi - CBD",
          billedAmount: 200000,
          paidAmount: 180000,
          outstanding: 20000,
          status: "UNPAID",
          paymentDate: null
        },
        {
          id: 5,
          month: "2024-11",
          revenueStream: "SBP",
          location: "Nakuru - Town",
          billedAmount: 120000,
          paidAmount: 120000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-11-25"
        },
        {
          id: 6,
          month: "2024-11",
          revenueStream: "Market Fees",
          location: "Nakuru - Town",
          billedAmount: 120000,
          paidAmount: 0,
          outstanding: 120000,
          status: "OVERDUE",
          paymentDate: null
        },
        {
          id: 7,
          month: "2024-10",
          revenueStream: "SBP",
          location: "Kisumu - Kondele",
          billedAmount: 95000,
          paidAmount: 95000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-10-18"
        },
        {
          id: 8,
          month: "2024-10",
          revenueStream: "Billboard",
          location: "Kisumu - Kondele",
          billedAmount: 75000,
          paidAmount: 75000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-10-22"
        },
        {
          id: 9,
          month: "2024-09",
          revenueStream: "SBP",
          location: "Nairobi - CBD",
          billedAmount: 145000,
          paidAmount: 145000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-09-15"
        },
        {
          id: 10,
          month: "2024-09",
          revenueStream: "Parking Fees",
          location: "Nairobi - CBD",
          billedAmount: 190000,
          paidAmount: 190000,
          outstanding: 0,
          status: "PAID",
          paymentDate: "2024-09-20"
        }
      ],

      filteredRevenueHistory: [],
      chart: null
    }
  },
  computed: {
    revenueStats() {
      const totalRevenue = this.filteredRevenueHistory.reduce((sum, record) => sum + record.paidAmount, 0);
      const averageMonthly = totalRevenue / Math.max(this.filters.period, 1);
      const paidRecords = this.filteredRevenueHistory.filter(r => r.status === 'PAID').length;
      const paymentRate = Math.round((paidRecords / Math.max(this.filteredRevenueHistory.length, 1)) * 100);

      return {
        totalRevenue,
        averageMonthly,
        growthRate: 12.5, // Mock growth rate
        paymentRate
      };
    },

    totals() {
      return {
        billed: this.filteredRevenueHistory.reduce((sum, record) => sum + record.billedAmount, 0),
        paid: this.filteredRevenueHistory.reduce((sum, record) => sum + record.paidAmount, 0),
        outstanding: this.filteredRevenueHistory.reduce((sum, record) => sum + record.outstanding, 0)
      };
    }
  },
  mounted() {
    this.filteredRevenueHistory = [...this.revenueHistory];
    this.initChart();
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    getStatusClass(status) {
      switch (status) {
        case 'PAID':
          return 'bg-success';
        case 'UNPAID':
          return 'bg-warning';
        case 'OVERDUE':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    filterData() {
      let filtered = [...this.revenueHistory];

      // Filter by period
      const monthsBack = parseInt(this.filters.period);
      const cutoffDate = moment().subtract(monthsBack, 'months').format('YYYY-MM');
      filtered = filtered.filter(record => record.month >= cutoffDate);

      // Filter by revenue stream
      if (this.filters.revenueStream) {
        filtered = filtered.filter(record => record.revenueStream === this.filters.revenueStream);
      }

      // Filter by location
      if (this.filters.location) {
        filtered = filtered.filter(record => record.location.includes(this.filters.location.split(' - ')[0]));
      }

      // Filter by status
      if (this.filters.status) {
        filtered = filtered.filter(record => record.status === this.filters.status);
      }

      this.filteredRevenueHistory = filtered.sort((a, b) => b.month.localeCompare(a.month));
      this.updateChart();
    },

    initChart() {
      // Placeholder for chart initialization
      // In a real implementation, you would use Chart.js or similar
      console.log('Chart initialized');
    },

    updateChart() {
      // Placeholder for chart update
      console.log('Chart updated');
    },

    downloadReport() {
      console.log('Downloading revenue history report for:', this.taxpayer.name);
      alert(`Downloading revenue history report for ${this.taxpayer.name}...`);
    }
  },
  watch: {
    taxpayer() {
      this.filterData();
    }
  }
}
</script>

<style scoped>
.table th {
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
}

.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
}

.chart-container {
  position: relative;
  width: 100%;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.chart-container::before {
  content: "Revenue Chart (Chart.js Integration Required)";
  font-size: 1.1rem;
  font-weight: 500;
}

.modal-xl {
  max-width: 1400px;
}

.border {
  border-color: #dee2e6 !important;
}
</style>